import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FLASH_PARAM } from '../lib/entityHref';

/**
 * N8 (86d3w2kez) part 1 — consumes the one-shot `?flash={jobId}` arrival marker.
 *
 * <b>Read once, then stripped</b> via `setSearchParams(…, { replace: true })`, so the marker never
 * survives into history. Without the strip, Back onto this page re-flashes a card the user has
 * already dealt with, and copying the URL to a colleague sends them a highlight meant for someone
 * else.
 *
 * ⚠️ <b>The value is held in state, not read from the URL.</b> Stripping the parameter and reading it
 * from the same source in the same pass is a race the strip always wins: the caller sees `null` on
 * the very render the highlight was supposed to start. This captures the value first and hands back a
 * stable answer for the life of the page.
 *
 * <b>Existing idiom, two flavours.</b> The quotation workspace already does one-shot params
 * (`?new=1` / `?review=1`, `QuotationWorkspacePage.tsx:180-201`) and this matches it. The sales-order
 * workspace has only id/filter URL-sync with a param-PRESERVING rewrite — which is why an extra
 * `?flash=` survives that rewrite, and why SO gets this consumption fresh rather than inheriting it.
 */
export function useFlashTarget(): number | null {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobId, setJobId] = useState<number | null>(null);

  useEffect(() => {
    const raw = searchParams.get(FLASH_PARAM);
    // ⚠️ No once-per-mount ref here, on purpose. SPA navigation onto an ALREADY-mounted page (the
    // same picklist failing again, clicked from the popover) delivers a fresh ?flash= with no
    // remount — a mount-scoped ref would neither consume nor strip it, leaving the marker in the
    // URL forever (2026-08-25 review). One-shot-ness comes from the strip itself: once removed, the
    // param is absent and this effect early-returns until a NEW marker arrives.
    if (raw === null) return;

    // A non-numeric marker is someone's hand-edited URL. Strip it and flash nothing — better than
    // passing NaN down to a comparison that quietly matches no batch.
    const parsed = Number(raw);
    if (Number.isInteger(parsed)) setJobId(parsed);

    const next = new URLSearchParams(searchParams);
    next.delete(FLASH_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return jobId;
}
